export const MEDIA_PREVIEW_CACHE_NAME = "msg.web.media_preview_cache.v3";
export const MEDIA_PREVIEW_CACHE_INDEX_KEY = "msg.web.media_preview_cache_index.v3";

const MEDIA_PREVIEW_CACHE_VERSION = 3;
const DEFAULT_TTL_MILLIS = 7 * 24 * 60 * 60 * 1000;
const DEFAULT_MAX_BYTES = 50 * 1024 * 1024;
const DEFAULT_MAX_ENTRIES = 200;
const SYNTHETIC_CACHE_ORIGIN = "https://msg.local";
const SYNTHETIC_CACHE_PATH_PREFIX = "/__msg_media_preview_cache__/v3";

export type MediaPreviewKind = "photo" | "voice";
export type MediaPreviewRepresentation = "inline_preview" | "playback";

export interface MediaPreviewCacheKeyInput {
  actorUserID: string;
  mediaID: string;
  kind: MediaPreviewKind;
  representation?: MediaPreviewRepresentation;
  gatewayOrigin?: string;
}

export interface MediaPreviewCachePolicy {
  nowMillis?: () => number;
  ttlMillis?: number;
  maxBytes?: number;
  maxEntries?: number;
}

interface MediaPreviewCacheIndexEntry {
  key: string;
  cacheURL: string;
  actorUserID: string;
  mediaID: string;
  kind: MediaPreviewKind;
  representation: MediaPreviewRepresentation;
  gatewayOrigin: string;
  sizeBytes: number;
  contentType: string;
  createdAtMillis: number;
  expiresAtMillis: number;
  lastAccessedAtMillis: number;
}

interface MediaPreviewCacheIndex {
  version: number;
  entries: Record<string, MediaPreviewCacheIndexEntry>;
}

interface ResolvedPolicy {
  nowMillis: () => number;
  ttlMillis: number;
  maxBytes: number;
  maxEntries: number;
}

function resolvePolicy(policy: MediaPreviewCachePolicy = {}): ResolvedPolicy {
  return {
    nowMillis: policy.nowMillis ?? (() => Date.now()),
    ttlMillis: normalizePositiveInteger(policy.ttlMillis, DEFAULT_TTL_MILLIS),
    maxBytes: normalizePositiveInteger(policy.maxBytes, DEFAULT_MAX_BYTES),
    maxEntries: normalizePositiveInteger(policy.maxEntries, DEFAULT_MAX_ENTRIES),
  };
}

function normalizePositiveInteger(value: number | undefined, fallback: number): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
    return fallback;
  }
  return Math.trunc(value);
}

function normalizeKeyPart(value: string): string {
  return value.trim();
}

function normalizeGatewayOrigin(value: string | undefined): string {
  const trimmed = value?.trim() ?? "";
  if (trimmed === "") {
    return "same-origin";
  }
  return trimmed.endsWith("/") ? trimmed.slice(0, -1) : trimmed;
}

function normalizeKind(value: unknown): MediaPreviewKind | null {
  if (value !== "photo" && value !== "voice") {
    return null;
  }
  return value;
}

function defaultRepresentation(kind: MediaPreviewKind): MediaPreviewRepresentation {
  return kind === "voice" ? "playback" : "inline_preview";
}

function normalizeRepresentation(value: unknown, kind: MediaPreviewKind): MediaPreviewRepresentation | null {
  if (value === undefined) {
    return defaultRepresentation(kind);
  }
  if (value !== "inline_preview" && value !== "playback") {
    return null;
  }
  return value;
}

function encodePathPart(value: string): string {
  return encodeURIComponent(value).replace(/[!'()*]/g, (character) => `%${character.charCodeAt(0).toString(16).toUpperCase()}`);
}

function resolveCacheURL(key: string): string {
  return `${SYNTHETIC_CACHE_ORIGIN}${key}`;
}

function readWindow(): (Window & typeof globalThis) | null {
  if (typeof window === "undefined") {
    return null;
  }
  return window;
}

function readStorage(): Storage | null {
  const runtimeWindow = readWindow();
  if (runtimeWindow === null) {
    return null;
  }
  try {
    return runtimeWindow.localStorage;
  } catch {
    return null;
  }
}

function readCacheStorage(): CacheStorage | null {
  const runtimeWindow = readWindow();
  if (runtimeWindow === null || runtimeWindow.caches === undefined) {
    return null;
  }
  return runtimeWindow.caches;
}

function normalizeIndexEntry(value: unknown, key: string): MediaPreviewCacheIndexEntry | null {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  const entry = value as Partial<MediaPreviewCacheIndexEntry>;
  const kind = normalizeKind(entry.kind);
  if (kind === null) {
    return null;
  }
  const representation =
    typeof entry.representation === "string"
      ? normalizeRepresentation(entry.representation, kind)
      : null;
  if (representation === null) {
    return null;
  }

  const cacheURL = typeof entry.cacheURL === "string" ? entry.cacheURL.trim() : "";
  const actorUserID = typeof entry.actorUserID === "string" ? entry.actorUserID.trim() : "";
  const mediaID = typeof entry.mediaID === "string" ? entry.mediaID.trim() : "";
  const gatewayOrigin = typeof entry.gatewayOrigin === "string" ? entry.gatewayOrigin.trim() : "";
  const sizeBytes = Number(entry.sizeBytes);
  const createdAtMillis = Number(entry.createdAtMillis);
  const expiresAtMillis = Number(entry.expiresAtMillis);
  const lastAccessedAtMillis = Number(entry.lastAccessedAtMillis);
  const contentType = typeof entry.contentType === "string" ? entry.contentType.trim() : "";

  if (
    cacheURL === "" ||
    actorUserID === "" ||
    mediaID === "" ||
    gatewayOrigin === "" ||
    !Number.isFinite(sizeBytes) ||
    sizeBytes <= 0 ||
    !Number.isFinite(createdAtMillis) ||
    !Number.isFinite(expiresAtMillis) ||
    !Number.isFinite(lastAccessedAtMillis)
  ) {
    return null;
  }

  return {
    key,
    cacheURL,
    actorUserID,
    mediaID,
    kind,
    representation,
    gatewayOrigin,
    sizeBytes: Math.trunc(sizeBytes),
    contentType,
    createdAtMillis: Math.trunc(createdAtMillis),
    expiresAtMillis: Math.trunc(expiresAtMillis),
    lastAccessedAtMillis: Math.trunc(lastAccessedAtMillis),
  };
}

function readIndex(): MediaPreviewCacheIndex {
  const storage = readStorage();
  if (storage === null) {
    return { version: MEDIA_PREVIEW_CACHE_VERSION, entries: {} };
  }

  try {
    const raw = storage.getItem(MEDIA_PREVIEW_CACHE_INDEX_KEY);
    if (raw === null || raw.trim() === "") {
      return { version: MEDIA_PREVIEW_CACHE_VERSION, entries: {} };
    }

    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
      return { version: MEDIA_PREVIEW_CACHE_VERSION, entries: {} };
    }

    const candidate = parsed as { version?: unknown; entries?: unknown };
    if (candidate.version !== MEDIA_PREVIEW_CACHE_VERSION || candidate.entries === null || typeof candidate.entries !== "object" || Array.isArray(candidate.entries)) {
      return { version: MEDIA_PREVIEW_CACHE_VERSION, entries: {} };
    }

    const entries: Record<string, MediaPreviewCacheIndexEntry> = {};
    for (const [key, value] of Object.entries(candidate.entries as Record<string, unknown>)) {
      const normalizedKey = key.trim();
      if (normalizedKey === "") {
        continue;
      }
      const normalizedEntry = normalizeIndexEntry(value, normalizedKey);
      if (normalizedEntry !== null) {
        entries[normalizedKey] = normalizedEntry;
      }
    }

    return { version: MEDIA_PREVIEW_CACHE_VERSION, entries };
  } catch {
    return { version: MEDIA_PREVIEW_CACHE_VERSION, entries: {} };
  }
}

function writeIndex(index: MediaPreviewCacheIndex): void {
  const storage = readStorage();
  if (storage === null) {
    return;
  }

  try {
    storage.setItem(MEDIA_PREVIEW_CACHE_INDEX_KEY, JSON.stringify(index));
  } catch {
    // Best-effort cache metadata; rendering must not depend on storage quota.
  }
}

function createRequest(cacheURL: string): Request {
  return new Request(cacheURL, { method: "GET" });
}

async function deleteCacheEntry(cacheURL: string): Promise<void> {
  const cacheStorage = readCacheStorage();
  if (cacheStorage === null) {
    return;
  }

  try {
    const cache = await cacheStorage.open(MEDIA_PREVIEW_CACHE_NAME);
    await cache.delete(createRequest(cacheURL));
  } catch {
    // Cache deletion is best-effort.
  }
}

function isExpired(entry: MediaPreviewCacheIndexEntry, nowMillis: number): boolean {
  return entry.expiresAtMillis <= nowMillis;
}

export function buildMediaPreviewCacheKey(input: MediaPreviewCacheKeyInput): string | null {
  const actorUserID = normalizeKeyPart(input.actorUserID);
  const mediaID = normalizeKeyPart(input.mediaID);
  const kind = normalizeKind(input.kind);
  if (actorUserID === "" || mediaID === "" || kind === null) {
    return null;
  }

  const representation = normalizeRepresentation(input.representation, kind);
  if (representation === null) {
    return null;
  }

  const gatewayOrigin = normalizeGatewayOrigin(input.gatewayOrigin);
  return [
    SYNTHETIC_CACHE_PATH_PREFIX,
    encodePathPart(gatewayOrigin),
    encodePathPart(actorUserID),
    kind,
    representation,
    encodePathPart(mediaID),
  ].join("/");
}

export async function readCachedMediaPreview(input: MediaPreviewCacheKeyInput, policy?: MediaPreviewCachePolicy): Promise<Blob | null> {
  const key = buildMediaPreviewCacheKey(input);
  if (key === null) {
    return null;
  }

  const resolvedPolicy = resolvePolicy(policy);
  const nowMillis = resolvedPolicy.nowMillis();
  const index = readIndex();
  const entry = index.entries[key];
  if (entry === undefined) {
    return null;
  }

  if (isExpired(entry, nowMillis)) {
    delete index.entries[key];
    writeIndex(index);
    await deleteCacheEntry(entry.cacheURL);
    return null;
  }

  const cacheStorage = readCacheStorage();
  if (cacheStorage === null) {
    return null;
  }

  try {
    const cache = await cacheStorage.open(MEDIA_PREVIEW_CACHE_NAME);
    const response = await cache.match(createRequest(entry.cacheURL));
    if (response === undefined) {
      delete index.entries[key];
      writeIndex(index);
      return null;
    }

    const blob = await response.blob();
    if (blob.size <= 0) {
      delete index.entries[key];
      writeIndex(index);
      await cache.delete(createRequest(entry.cacheURL));
      return null;
    }

    index.entries[key] = {
      ...entry,
      sizeBytes: blob.size,
      contentType: blob.type || entry.contentType,
      lastAccessedAtMillis: nowMillis,
    };
    writeIndex(index);
    return blob;
  } catch {
    return null;
  }
}

export async function writeCachedMediaPreview(input: MediaPreviewCacheKeyInput, blob: Blob, policy?: MediaPreviewCachePolicy): Promise<void> {
  const key = buildMediaPreviewCacheKey(input);
  if (key === null || blob.size <= 0) {
    return;
  }

  const cacheStorage = readCacheStorage();
  if (cacheStorage === null) {
    return;
  }

  const resolvedPolicy = resolvePolicy(policy);
  const nowMillis = resolvedPolicy.nowMillis();
  const gatewayOrigin = normalizeGatewayOrigin(input.gatewayOrigin);
  const representation = normalizeRepresentation(input.representation, input.kind) ?? defaultRepresentation(input.kind);
  const cacheURL = resolveCacheURL(key);
  const response = new Response(blob, {
    headers: blob.type === "" ? undefined : { "content-type": blob.type },
  });

  try {
    const cache = await cacheStorage.open(MEDIA_PREVIEW_CACHE_NAME);
    await cache.put(createRequest(cacheURL), response);

    const index = readIndex();
    index.entries[key] = {
      key,
      cacheURL,
      actorUserID: normalizeKeyPart(input.actorUserID),
      mediaID: normalizeKeyPart(input.mediaID),
      kind: input.kind,
      representation,
      gatewayOrigin,
      sizeBytes: blob.size,
      contentType: blob.type,
      createdAtMillis: nowMillis,
      expiresAtMillis: nowMillis + resolvedPolicy.ttlMillis,
      lastAccessedAtMillis: nowMillis,
    };
    writeIndex(index);
    await evictMediaPreviewCache(policy);
  } catch {
    // Cache writes are opportunistic; network previews should still render.
  }
}

export async function deleteCachedMediaPreview(input: MediaPreviewCacheKeyInput): Promise<void> {
  const key = buildMediaPreviewCacheKey(input);
  if (key === null) {
    return;
  }

  const index = readIndex();
  const entry = index.entries[key];
  if (entry !== undefined) {
    delete index.entries[key];
    writeIndex(index);
    await deleteCacheEntry(entry.cacheURL);
    return;
  }

  await deleteCacheEntry(resolveCacheURL(key));
}

export async function evictMediaPreviewCache(policy?: MediaPreviewCachePolicy): Promise<void> {
  const resolvedPolicy = resolvePolicy(policy);
  const nowMillis = resolvedPolicy.nowMillis();
  const index = readIndex();
  const entries = Object.values(index.entries);
  const keysToDelete = new Set<string>();

  for (const entry of entries) {
    if (isExpired(entry, nowMillis)) {
      keysToDelete.add(entry.key);
    }
  }

  let retained = entries.filter((entry) => !keysToDelete.has(entry.key));
  let totalBytes = retained.reduce((total, entry) => total + entry.sizeBytes, 0);

  retained = retained.sort((left, right) => left.lastAccessedAtMillis - right.lastAccessedAtMillis);
  while (retained.length > resolvedPolicy.maxEntries || totalBytes > resolvedPolicy.maxBytes) {
    const evicted = retained.shift();
    if (evicted === undefined) {
      break;
    }
    keysToDelete.add(evicted.key);
    totalBytes -= evicted.sizeBytes;
  }

  if (keysToDelete.size === 0) {
    return;
  }

  for (const key of keysToDelete) {
    const entry = index.entries[key];
    if (entry !== undefined) {
      delete index.entries[key];
      await deleteCacheEntry(entry.cacheURL);
    }
  }

  writeIndex(index);
}
